import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, Search, Plus, Edit, Trash2, Eye, 
  EyeOff, Star, ExternalLink, RefreshCw, Layers, CheckCircle2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import API from '../../config/api';

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const base = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    return `${base}${url}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API}/products?limit=100&include_inactive=true`, { withCredentials: true }),
        axios.get(`${API}/categories?all=true`)
      ]);

      const prodList = prodRes.data?.products || (Array.isArray(prodRes.data) ? prodRes.data : []);
      setProducts(prodList);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Quick Visibility Toggle (Point 5)
  const handleToggleVisibility = async (productId) => {
    try {
      const res = await axios.patch(`${API}/products/${productId}/visibility`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, is_active: res.data.is_active } : p));
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to toggle visibility');
    }
  };

  // Soft Delete Handler (Point 11)
  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.name}"? (Soft delete)`)) return;

    try {
      await axios.delete(`${API}/products/${product.id}`, { withCredentials: true });
      toast.success('Product soft-deleted successfully');
      setProducts(prev => prev.filter(p => p.id !== product.id));
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.slug?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      
      {/* ─── Top Control Header ─── */}
      <div className="bg-white rounded-2xl p-5 border border-[#1A1A1A]/[0.08] shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-5 h-5 text-[#D9711F]" />
            Products Catalog
          </h1>
          <p className="text-xs text-gray-500">
            Total {products.length} products &middot; {products.filter(p => p.is_active).length} live on store
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link to="/admin/categories">
            <Button variant="outline" className="text-xs font-bold flex items-center gap-1.5 rounded-xl border-gray-200">
              <Layers className="w-4 h-4 text-[#6E1B34]" />
              Manage Categories
            </Button>
          </Link>
          
          <Link to="/admin/products/new">
            <Button className="btn-saffron text-xs font-bold flex items-center gap-1.5 px-4 py-2 rounded-xl shadow-xs">
              <Plus className="w-4 h-4" />
              Add New Product
            </Button>
          </Link>
        </div>
      </div>

      {/* ─── Search & Category Filters Bar ─── */}
      <div className="bg-white rounded-2xl p-4 border border-[#1A1A1A]/[0.08] shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by product name, slug, or keyword..."
            className="pl-9 text-xs"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-gray-600">Category:</span>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-xs font-medium text-gray-800 bg-white focus:outline-none focus:border-[#D9711F]"
          >
            <option value="all">All Categories ({products.length})</option>
            {categories.map((c) => (
              <option key={c.id} value={c.slug || c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ─── Products Table ─── */}
      <div className="bg-white rounded-2xl border border-[#1A1A1A]/[0.08] shadow-xs overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-16">
            <div className="text-center">
              <div className="w-8 h-8 border-3 border-[#6E1B34] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-xs text-gray-500 font-semibold">Loading products catalog...</p>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center p-16">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-700">No products found</p>
            <p className="text-xs text-gray-400 mt-1">Try changing your search or add a new product</p>
            <Link to="/admin/products/new" className="inline-block mt-4">
              <Button className="btn-saffron text-xs font-bold">
                <Plus className="w-4 h-4 mr-1.5" />
                Add First Product
              </Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 font-bold uppercase tracking-wider">
                <tr>
                  <th className="py-3.5 px-4">Product</th>
                  <th className="py-3.5 px-4">Category</th>
                  <th className="py-3.5 px-4">Price / Sale</th>
                  <th className="py-3.5 px-4 text-center">Stock</th>
                  <th className="py-3.5 px-4 text-center">Bestseller</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-medium text-gray-800">
                {filteredProducts.map((product) => {
                  const regularPrice = product.regular_price || product.price;
                  const salePrice = product.sale_price;
                  const effectivePrice = salePrice && salePrice > 0 ? salePrice : regularPrice;
                  const mrp = salePrice && salePrice > 0 ? regularPrice : Math.round(regularPrice * 1.35);

                  return (
                    <tr key={product.id} className="hover:bg-gray-50/70 transition-colors">
                      
                      {/* Product Thumbnail & Name */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200">
                            <img
                              src={resolveUrl(product.image_url || product.images?.[0])}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-gray-900 hover:text-[#6E1B34] transition-colors line-clamp-1">
                                {product.name}
                              </h3>
                              <a
                                href={`/store/${product.slug || product.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-gray-400 hover:text-[#D9711F]"
                                title="View on Store"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            </div>
                            <span className="text-[11px] text-gray-500 font-mono">
                              /store/{product.slug || product.id}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Category */}
                      <td className="py-3.5 px-4">
                        <span className="bg-[#FBF4E7] text-[#4E1225] font-semibold px-2.5 py-1 rounded-lg border border-[#E7D9BC] text-[11px]">
                          {product.category_name || product.category}
                        </span>
                      </td>

                      {/* Price / Sale */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-baseline gap-1.5">
                          <span className="font-bold text-gray-900 text-sm">
                            ₹{effectivePrice.toLocaleString('en-IN')}
                          </span>
                          {mrp > effectivePrice && (
                            <span className="text-[11px] text-gray-400 line-through">
                              ₹{mrp.toLocaleString('en-IN')}
                            </span>
                          )}
                        </div>
                        {product.discount > 0 && (
                          <span className="text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                            {product.discount}% OFF
                          </span>
                        )}
                      </td>

                      {/* Stock */}
                      <td className="py-3.5 px-4 text-center">
                        <span className={`font-bold text-xs ${
                          product.stock > 10 ? 'text-gray-800' : product.stock > 0 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {product.stock || 0}
                        </span>
                      </td>

                      {/* Bestseller Badge */}
                      <td className="py-3.5 px-4 text-center">
                        {product.bestseller ? (
                          <span className="inline-flex items-center gap-1 bg-[#FCEEDF] text-[#B85A15] border border-[#F2A65A]/40 font-bold px-2 py-0.5 rounded-md text-[10px]">
                            <Star className="w-3 h-3 fill-current text-[#D9711F]" />
                            Bestseller
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Status / Visibility Toggle */}
                      <td className="py-3.5 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleToggleVisibility(product.id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${
                            product.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                          title="Click to toggle visibility"
                        >
                          {product.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          <span>{product.is_active ? 'Live' : 'Hidden'}</span>
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/admin/products/edit/${product.id}`}>
                            <button
                              type="button"
                              className="p-2 hover:bg-gray-100 rounded-xl text-gray-700 hover:text-[#6E1B34] transition-colors border border-transparent hover:border-gray-200"
                              title="Edit Product"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </Link>
                          
                          <button
                            type="button"
                            onClick={() => handleDeleteProduct(product)}
                            className="p-2 hover:bg-red-50 rounded-xl text-red-500 hover:text-red-700 transition-colors border border-transparent hover:border-red-100"
                            title="Soft Delete Product"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default AdminProducts;
